import { CallGroup, formatTrack, formatTime } from '../../hooks/useTranscriptions';

interface CallCardProps {
  group: CallGroup;
}

export default function CallCard({ group }: CallCardProps) {
  const initials = (group.caller_name || 'UN')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      className={`rounded-lg border overflow-hidden mb-3 ${
        group.isLive
          ? 'border-teal-400 shadow-sm shadow-teal-100'
          : 'border-gray-200'
      }`}
    >
      {/* Call header */}
      <div className="flex items-center justify-between bg-white px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${
              group.isLive
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {initials}
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold text-gray-900">
              {group.caller_name || 'Unknown Caller'}
            </div>
            <div className="text-xs text-gray-400">
              {group.transcripts.filter((t) => !t.is_partial).length} messages
            </div>
          </div>
        </div>
        {group.isLive ? (
          <span className="flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-600">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
            LIVE
          </span>
        ) : (
          <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
            Ended
          </span>
        )}
      </div>

      {/* Transcript messages */}
      <div className="bg-gray-50 px-4 py-3 space-y-3">
        {group.transcripts.map((t, i) => (
          <div key={t.id || `partial-${t.track}-${i}`}>
            <div
              className={`mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide ${
                t.track === 'inbound_track'
                  ? 'text-blue-600'
                  : 'text-purple-600'
              }`}
            >
              <span
                className={`inline-block h-2.5 w-2.5 rounded-sm ${
                  t.track === 'inbound_track' ? 'bg-blue-200' : 'bg-purple-200'
                }`}
              />
              {formatTrack(t.track)}
              {(t.is_partial || t._inlinePartial) && (
                <span className="animate-pulse font-normal normal-case italic text-gray-400">
                  live
                </span>
              )}
            </div>
            <div
              className={`rounded-lg px-3 py-2 text-sm leading-relaxed ${
                t.is_partial
                  ? 'border border-dashed border-yellow-300 bg-yellow-50 text-yellow-900'
                  : t.track === 'inbound_track'
                  ? 'border border-blue-100 bg-blue-50 text-gray-800'
                  : 'border border-purple-100 bg-purple-50 text-gray-800'
              }`}
            >
              {t.transcript}
              {t._inlinePartial && (
                <span className="opacity-60">
                  {' ' + t._inlinePartial.transcript}
                </span>
              )}
              {(t.is_partial || t._inlinePartial) && (
                <span className="ml-0.5 animate-pulse font-bold text-teal-500">
                  |
                </span>
              )}
            </div>
            {!t.is_partial && t.created_at && (
              <div className="mt-1 text-right text-[10px] text-gray-300">
                {formatTime(t.created_at)}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Call ended bar */}
      {!group.isLive && (
        <div className="border-t border-gray-200 bg-gray-100 px-4 py-1.5 text-center text-xs text-gray-400">
          Call ended at {formatTime(group.endTime)} &middot;{' '}
          {group.transcripts.filter((t) => !t.is_partial).length} segments
        </div>
      )}
    </div>
  );
}
